import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../lib/auth";
import { useEvents } from "../../lib/events";
import NotificationsBell from "./NotificationsBell";
import {
  LayoutDashboard, Users, Mic2, CalendarDays, MessageSquare, BarChart3,
  FileText, Settings as SettingsIcon, Skull, LogOut, Theater, ClipboardList, Wallet,
  ChevronDown, Archive, Plus, List, Bell, Drama, Sparkles, ClipboardCheck,
} from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/admin/applicants", icon: Users, label: "Applicants", testid: "nav-applicants" },
  { to: "/admin/cast", icon: Drama, label: "Cast", testid: "nav-cast" },
  { to: "/admin/auditions", icon: Mic2, label: "Auditions", testid: "nav-auditions" },
  { to: "/admin/rehearsals", icon: ClipboardCheck, label: "Rehearsals", testid: "nav-rehearsals" },
  { to: "/admin/roles", icon: Theater, label: "Roles", testid: "nav-roles" },
  { to: "/admin/role-match", icon: Sparkles, label: "Role Match", testid: "nav-role-match" },
  { to: "/admin/show-dates", icon: CalendarDays, label: "Open Dates", testid: "nav-show-dates" },
  { to: "/admin/roster", icon: ClipboardList, label: "Roster", testid: "nav-roster" },
  { to: "/admin/notifications", icon: Bell, label: "Notifications", testid: "nav-notifications" },
  { to: "/admin/talent-pool", icon: Users, label: "Talent Pool", testid: "nav-talent-pool" },
  { to: "/admin/messages", icon: MessageSquare, label: "Messages", testid: "nav-messages" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports", testid: "nav-reports" },
  { to: "/admin/payroll", icon: Wallet, label: "Payroll", testid: "nav-payroll" },
  { to: "/admin/templates", icon: FileText, label: "Templates", testid: "nav-templates" },
  { to: "/admin/form-builder", icon: FileText, label: "Form Builder", testid: "nav-form-builder" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings" },
];

const STATUS_COLOR = { active: "#10B981", closed: "#F59E0B", archived: "#6B6862" };

function EventSwitcher() {
  const { events, currentEvent, setCurrentEvent } = useEvents();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (events.length === 0) {
    return (
      <div className="px-4 py-3 border-b border-[#1a1a1a] shrink-0">
        <div className="label-mini mb-1.5">Current Event</div>
        <button data-testid="event-add-first" onClick={() => navigate("/admin/events?new=1")}
          className="w-full flex items-center justify-center gap-2 bg-blood text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-[#FF3333] transition-colors">
          <Plus size={14} /> Add Event
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b border-[#1a1a1a] shrink-0 relative">
      <div className="label-mini mb-1.5">Current Event</div>
      <button data-testid="event-switcher-btn" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 bg-[#050505] border border-[#262626] px-3 py-2 text-sm hover:border-blood transition-colors">
        <span className="flex items-center gap-2 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STATUS_COLOR[currentEvent?.status] || "#6B6862" }} />
          <span className="truncate text-left" data-testid="current-event-name">{currentEvent?.name || "No event"}</span>
        </span>
        <ChevronDown size={14} className={`shrink-0 text-[#6B6862] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-4 right-4 mt-1 z-50 bg-[#0A0A0A] border border-[#262626] max-h-80 overflow-y-auto shadow-2xl">
            {events.map((ev) => (
              <button key={ev.id} data-testid={`event-switch-${ev.id}`} onClick={() => { setCurrentEvent(ev.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 text-sm hover:bg-[#121212] flex items-center gap-2 transition-colors ${ev.id === currentEvent?.id ? "text-blood bg-[#101010]" : "text-[#A19D94]"}`}>
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STATUS_COLOR[ev.status] }} />
                <span className="truncate">{ev.name}</span>
              </button>
            ))}
            <div className="border-t border-[#262626]">
              <button data-testid="event-add-new" onClick={() => { setOpen(false); navigate("/admin/events?new=1"); }}
                className="w-full text-left px-3 py-2.5 text-xs uppercase tracking-wider text-blood hover:bg-[#121212] flex items-center gap-2 transition-colors">
                <Plus size={14} /> Add New Event
              </button>
              <button data-testid="event-view-all" onClick={() => { setOpen(false); navigate("/admin/events"); }}
                className="w-full text-left px-3 py-2.5 text-xs uppercase tracking-wider text-[#A19D94] hover:bg-[#121212] flex items-center gap-2 transition-colors">
                <List size={14} /> View All Events
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAdminAuth();
  const { isArchived } = useEvents();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#050505] text-[#F2EBE1]">
      {/* Sidebar — sticky to viewport, scrolls internally if needed */}
      <aside className="w-60 shrink-0 border-r border-[#1a1a1a] bg-[#0A0A0A] flex flex-col sticky top-0 h-screen overflow-y-auto">
        <Link to="/admin" className="px-6 py-7 border-b border-[#1a1a1a] block shrink-0" data-testid="sidebar-logo">
          <div className="flex items-center gap-3">
            <Skull size={28} className="text-blood" strokeWidth={1.5} />
            <div>
              <div className="font-display text-2xl font-bold leading-none tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></div>
              <div className="label-mini mt-1">Scare Actor Apps</div>
            </div>
          </div>
        </Link>
        <EventSwitcher />
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  isActive ? "text-[#F2EBE1] bg-[#121212]" : "text-[#8a857d] hover:text-[#F2EBE1] hover:bg-[#101010]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-blood" />}
                  <Icon size={16} strokeWidth={1.6} />
                  <span className="tracking-wide">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1a1a1a] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1a1a1a] border border-[#262626] flex items-center justify-center font-display text-blood">
              {user?.name?.[0] || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" data-testid="admin-user-name">{user?.name || "Admin"}</div>
              <div className="label-mini">Administrator</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="p-1.5 text-[#6B6862] hover:text-blood transition-colors"
              data-testid="admin-logout-btn"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {isArchived && (
          <div data-testid="archived-banner" className="bg-[#6B6862]/15 border-b border-[#6B6862]/40 px-8 py-2.5 text-xs uppercase tracking-wider text-[#A19D94] flex items-center gap-2">
            <Archive size={13} /> This event is archived — read only. Restore it from the Events page to make changes.
          </div>
        )}
        <header className="px-8 py-6 border-b border-[#1a1a1a] flex items-center justify-between sticky top-0 bg-[#050505]/95 backdrop-blur z-10">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight uppercase leading-none" data-testid="page-title">{title}</h1>
            {subtitle && <p className="text-sm text-[#8a857d] mt-2">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3"><NotificationsBell />{actions}</div>
        </header>
        <div className="flex-1 p-8 animate-fade-in">{children}</div>
      </main>
    </div>
  );
}
